import { debounce } from '../utils/utils';

declare global {
  interface Window {
    en: boolean;
  }
}

const en = window.en;

export class NotificationContainerHandler {
  private notificationMessage: HTMLElement | null;
  private clearErrorMessageCallback?: () => void;

  constructor(container: HTMLElement) {
    this.notificationMessage = container.querySelector('.notification-container');
  }

  public setValidateError(formFieldErrors: (HTMLInputElement | HTMLSelectElement)[], submitButton: HTMLButtonElement | null) {
    const message = en ? 'There is an error. Please correct the following fields: ' : 'Es liegt ein Fehler vor. Bitte korrigiere folgende Felder: ' + getErrorFieldNames(formFieldErrors);
    this.setAriaMessage(message, formFieldErrors, submitButton);
  }

  public setAriaMessage(message: string, formFieldErrors: (HTMLInputElement | HTMLSelectElement)[], submitButton: HTMLButtonElement | null, keepDisabled = false) {
    if (this.notificationMessage) {
      this.notificationMessage.textContent = message;
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-label', this.notificationMessage?.textContent);
      }
      setTimeout(() => {
        this.notificationMessage!.setAttribute('aria-hidden', 'true');
      }, 300);
      if (!keepDisabled) {
        this.addClearErrorMessageEventListeners(formFieldErrors, submitButton);
      }
    }
  }

  private addClearErrorMessageEventListeners(formFieldErrors: (HTMLInputElement | HTMLSelectElement)[], submitButton: HTMLButtonElement | null) {
    this.clearErrorMessageCallback = () => this.clearErrorMessage(formFieldErrors, submitButton);
    formFieldErrors.forEach(formFieldError => {
      inputEvents.forEach(event => {
        if (this.clearErrorMessageCallback) {
          formFieldError.addEventListener(event, this.clearErrorMessageCallback);
        }
      });
    });
  }

  private clearErrorMessage(formFieldErrors: (HTMLInputElement | HTMLSelectElement)[], submitButton: HTMLButtonElement | null) {
    if (this.notificationMessage) {
      this.notificationMessage.textContent = '';
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-label');
      }
      this.notificationMessage.removeAttribute('aria-hidden');
      formFieldErrors.forEach(formFieldError => {
        inputEvents.forEach(event => {
          if (this.clearErrorMessageCallback) {
            formFieldError.addEventListener(event, this.clearErrorMessageCallback);
          }
        });
      });
    }
  }
}

export default class FormMicroLibrary {
  private slides: HTMLElement[];
  private currentSlide: HTMLElement;
  private slideChangeArgs: any[];
  private notificationHandler: NotificationContainerHandler;

  constructor(
    private container: HTMLElement,
    private onSlideChange?: (currentSlide: HTMLElement, nextSlide: HTMLElement, ...args: any[]) => void | false,
    ...args: any[]
  ) {
    this.notificationHandler = new NotificationContainerHandler(container);
    this.slides = Array.from(this.container.querySelectorAll('.form-slide'));
    this.currentSlide = this.slides.find(slide => slide.classList.contains('slide-active')) as HTMLElement;
    this.currentSlide.tabIndex = 0;
    this.updateContainerHeight(this.currentSlide);
    this.slideChangeArgs = args;

    this.slides.forEach((slide, index) => {
      if (index !== 0) {
        const prevSlide = this.slides[index - 1];

        slide.querySelector('button.btn-prev')?.addEventListener('click', () => {
          this.switchSlide(prevSlide);
        });
      }
    });

    window.addEventListener(
      'resize',
      debounce(() => this.updateContainerHeight(), 300)
    );
  }

  public switchSlide(nextSlide: HTMLElement, executeOnSlideChange = true): void {
    let shouldSwitch = true;
    if (executeOnSlideChange && this.onSlideChange && typeof this.onSlideChange === 'function') {
      shouldSwitch = this.onSlideChange(this.currentSlide, nextSlide, ...this.slideChangeArgs) !== false;
    }
    if (shouldSwitch) {
      this.currentSlide.classList.remove('slide-active');
      this.currentSlide.removeAttribute('tabindex');
      nextSlide.addEventListener('transitionend', event => transitionHandler(event, nextSlide));
      nextSlide.classList.add('slide-active');
      nextSlide.tabIndex = 0;
      this.currentSlide = nextSlide;
      this.updateContainerHeight(nextSlide);
    }
  }

  public updateContainerHeight(nextSlide?: HTMLElement): void {
    this.container.style.height = (nextSlide ? nextSlide : this.currentSlide).offsetHeight + 'px';
  }

  getNotificationHandler(): NotificationContainerHandler {
    return this.notificationHandler;
  }
}

function transitionHandler(event: TransitionEvent, nextSlide: HTMLElement) {
  if (event.propertyName === 'opacity' && event.target && (event.target as HTMLElement).classList.contains('slide-active')) {
    nextSlide.focus({ preventScroll: true });
    event.target.removeEventListener('transitionend', () => transitionHandler(event, nextSlide));
  }
}

const inputEvents = ['change', 'input'];

function getErrorFieldNames(formFieldErrors: (HTMLInputElement | HTMLSelectElement)[]): string {
  const fieldsArray: string[] = [];

  for (const formField of formFieldErrors) {
    if (formField.tagName === 'INPUT') {
      switch (formField.name) {
        case 'first_name':
          fieldsArray.push(en ? 'First Name' : 'Vorname');
          break;
        case 'last_name':
          fieldsArray.push(en ? 'Last Name' : 'Nachname');
          break;
        case 'date_of_birth':
          fieldsArray.push(en ? 'Date of birth' : 'Geburtsdatum');
          break;
        case 'phone_number':
        case 'phone':
          fieldsArray.push(en ? 'Phone number' : 'Telefonnummer');
          break;
        case 'email':
          fieldsArray.push(en ? 'Email address' : 'E-Mail-Adresse');
          break;
        case 'iban':
          fieldsArray.push('IBAN');
          break;
      }
    } else if (formField.tagName === 'SELECT') {
      if (formField.classList.contains('dateSelect')) {
        fieldsArray.push(en ? 'Date' : 'Datum');
      } else if (formField.classList.contains('timeSelect')) {
        fieldsArray.push(en ? 'Time' : 'Uhrzeit');
      }
    }
  }
  return fieldsArray.join(', ');
}
